import { useEffect, useMemo, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Table } from '@factorialco/deprecated-design-system'
import {
  CreateRoleLineItemDocument,
  CreateSimpleLineItemDocument,
  DeleteLineItemDocument,
  QuotePricingPageDocument,
  UpdateRoleLineItemDocument,
  UpdateSimpleLineItemDocument,
} from 'generated/resources/graphql'
import debounce from 'lodash/debounce'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { QuotePricingLineItem, profitPercentage } from 'modules/finance/components/Quotes/utils'

import Header from './Header'
import Body from './_Body'

interface QuotePhasesTableProps {
  currency: string
  lineItems: QuotePricingLineItem[]
  isProductQuote: boolean
  isDraftStatus: boolean
  isJobCatalogTreeEnabled: boolean
  legalEntityCountry: string | undefined
  quoteId: number
}

const QuotePhasesTable = ({
  currency,
  lineItems,
  isProductQuote,
  isDraftStatus,
  isJobCatalogTreeEnabled,
  legalEntityCountry,
  quoteId,
}: QuotePhasesTableProps) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const apolloClient = useApolloClient()
  const [standaloneItems, setStandaloneItems] = useState<QuotePricingLineItem[]>(lineItems)
  const discountPercentage = 0 // TODO: quote.discountPercentage

  useEffect(() => {
    setStandaloneItems(lineItems)
  }, [lineItems])

  // Simple line items
  const [createSimpleLineItem] = useMutation(CreateSimpleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.createSimpleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })
  const [updateSimpleLineItem] = useMutation(UpdateSimpleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.updateSimpleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })
  const [deleteLineItem] = useMutation(DeleteLineItemDocument, {
    extractorFn: (data) => data.financeMutations.deleteFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })

  // Role line items
  const [createRoleLineItem] = useMutation(CreateRoleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.createRoleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })
  const [updateRoleLineItem] = useMutation(UpdateRoleLineItemDocument, {
    extractorFn: (data) => data.financeMutations.updateRoleFinanceLineItem,
    refetchQueries: [QuotePricingPageDocument],
  })

  const updateSimpleLineItemRef = useRef(updateSimpleLineItem)
  useEffect(() => {
    updateSimpleLineItemRef.current = updateSimpleLineItem
  }, [updateSimpleLineItem])

  const updateRoleLineItemRef = useRef(updateRoleLineItem)
  useEffect(() => {
    updateRoleLineItemRef.current = updateRoleLineItem
  }, [updateRoleLineItem])

  const standaloneItemsRef = useRef(standaloneItems)
  useEffect(() => {
    standaloneItemsRef.current = standaloneItems
  }, [standaloneItems])

  const update = async (updatedItem: QuotePricingLineItem, outdatedItem: QuotePricingLineItem) => {
    const currentItem = standaloneItemsRef.current.find((item) => item.id === updatedItem.id)
    if (
      currentItem == null ||
      updatedItem.quantity == null ||
      updatedItem.unitPriceCents == null ||
      updatedItem.unitCostCents == null
    )
      return { success: false, errors: [] }

    let handled: ReturnType<typeof handleResult<unknown>>
    if (updatedItem.type === 'role') {
      const result = await updateRoleLineItemRef.current({
        variables: {
          id: updatedItem.id,
          quantity: updatedItem.quantity,
          oldLevelId: updatedItem.oldJobCatalogLevelId,
          treeLevelId: updatedItem.jobCatalogTreeLevelId,
        },
      })
      handled = handleResult(result, () => undefined)
    } else {
      const result = await updateSimpleLineItemRef.current({
        variables: {
          id: updatedItem.id,
          discountPercentage,
          description: updatedItem.description,
          quantity: updatedItem.quantity,
          unitPriceCents: updatedItem.unitPriceCents,
          unitCostCents: updatedItem.unitCostCents,
          taxRateId: updatedItem.taxRateId,
        },
      })
      handled = handleResult(result, () => undefined)
    }
    return handled
  }

  const debouncedUpdate = useMemo(
    () =>
      debounce(async (updatedItem: QuotePricingLineItem, outdatedItem: QuotePricingLineItem) => {
        const handled = await update(updatedItem, outdatedItem)
        if (!handled.success) {
          revertOptimisticState(outdatedItem, 'update')
          app.setFlash({
            type: 'wrong',
            message: i18n.t('common.save_error'),
            time: 2000,
          })
        }
        // TODO: if success, we dont use the response to update state cause we refetch the page query
        // Review if we can avoid that refetching and updateState with the mutation response
      }, 1000),
    []
  )

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel()
    }
  }, [debouncedUpdate])

  const updateQuoteApolloCachedData = (items: QuotePricingLineItem[]) => {
    const baseAmountCents = items.reduce((sum, item) => sum + (item.basePriceCents || 0), 0)
    const taxesTotalAmountCents = items.reduce(
      (sum, item) =>
        sum +
        ((item.basePriceCents || 0) - (item.basePriceCents || 0) * discountPercentage) *
          (item.taxRatePercentage || 0),
      0
    )
    const totalAmountCents = baseAmountCents + taxesTotalAmountCents
    const baseCostCents = items.reduce((sum, item) => sum + (item.baseCostCents || 0), 0)
    const totalHours = items
      .filter((item) => item.quantityType === 'hours')
      .reduce((sum, item) => sum + (item.quantity || 0), 0)
    const totalProfitCents = totalAmountCents - baseCostCents
    const totalProfitPercentage = profitPercentage(totalProfitCents, baseAmountCents)

    const cachedData = apolloClient.readQuery({
      query: QuotePricingPageDocument,
      variables: { id: quoteId, companyId: Number(companyId) },
    })

    if (cachedData?.finance?.quote) {
      apolloClient.writeQuery({
        query: QuotePricingPageDocument,
        variables: { id: quoteId, companyId: Number(companyId) },
        data: {
          ...cachedData,
          finance: {
            ...cachedData.finance,
            quote: {
              ...cachedData.finance.quote,
              baseAmountCents,
              totalAmountCents,
              baseCostCents,
              totalHours,
              taxesTotalAmountCents,
              totalProfitCents,
              totalProfitPercentage,
            },
          },
        },
      })
    }
  }

  const updateStateOptimistically = (newItems: QuotePricingLineItem[], updateQuote: boolean) => {
    setStandaloneItems(newItems)
    if (updateQuote) {
      updateQuoteApolloCachedData(newItems)
    }
  }

  const revertOptimisticState = (
    handledItem: QuotePricingLineItem,
    mutationType: 'create' | 'delete' | 'update'
  ) => {
    let newItems: QuotePricingLineItem[] = []
    if (mutationType === 'create') {
      newItems = standaloneItems.filter((item) => item.id !== handledItem.id)
    } else if (mutationType === 'delete') {
      newItems = standaloneItems
    } else if (mutationType === 'update') {
      newItems = standaloneItems.map((item) => (item.id === handledItem.id ? handledItem : item))
    }
    setStandaloneItems(newItems)
    updateQuoteApolloCachedData(newItems)
  }

  const buildUpdatedItem = (
    item: QuotePricingLineItem,
    newValues: Partial<QuotePricingLineItem>
  ): QuotePricingLineItem => {
    // TODO: calculate basePriceCents, baseCostCents, profitCents here
    return {
      ...item,
      ...newValues,
    }
  }

  const handleUpdateStandaloneItem = async (
    itemId: number,
    newValues: Partial<QuotePricingLineItem>
  ) => {
    const outdatedItem = standaloneItems.find((item) => item.id === itemId)
    if (!outdatedItem) return

    const updatedItem = buildUpdatedItem(outdatedItem, newValues)
    const updatedItems = standaloneItems.map((item) => (item.id === itemId ? updatedItem : item))
    // We only update quote state in case of simple.
    // In case of role, updatedItem wont have price/cost. We dont calculate that in frontend
    // so nothing changes in the quote
    updateStateOptimistically(updatedItems, updatedItem.type === 'simple')
    debouncedUpdate(updatedItem, outdatedItem)
  }

  const buildNewItem = (itemType: 'role' | 'simple'): QuotePricingLineItem => {
    return {
      id: Date.now() * -1,
      description: '-',
      quantity: 1,
      unitPriceCents: 0,
      baseCostCents: 0,
      basePriceCents: 0,
      unitCostCents: 0,
      taxRatePercentage: 0,
      baseProfitPercentage: 0,
      parentId: null,
      quantityType: 'units' as const,
      taxRateId: null,
      type: itemType,
    }
  }

  const handleAddStandaloneItem = async (itemType: 'role' | 'simple') => {
    const newItem = buildNewItem(itemType)
    updateStateOptimistically([...standaloneItems, newItem], false)
    const handled = await create(newItem)
    if (!handled.success) {
      revertOptimisticState(newItem, 'create')

      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
        time: 2000,
      })
    }
    // TODO: if success, we dont use the response to update state cause we refetch the page query
    // Review if we can avoid that refetching and updateState with the mutation response
  }

  const create = async (newItem: QuotePricingLineItem) => {
    if (
      newItem.quantity == null ||
      newItem.unitPriceCents == null ||
      newItem.unitCostCents == null
    ) {
      return { success: false, errors: [] }
    }

    let handled: ReturnType<typeof handleResult<unknown>>

    if (newItem.type === 'simple') {
      const result = await createSimpleLineItem({
        variables: {
          description: newItem.description,
          discountPercentage,
          quantity: newItem.quantity,
          sourceId: quoteId,
          sourceType: 'quote',
          unitPriceCents: newItem.unitPriceCents,
          unitCostCents: newItem.unitCostCents,
        },
      })
      handled = handleResult(result, () => undefined)
    } else {
      const result = await createRoleLineItem({
        variables: {
          quantity: 1,
          sourceId: quoteId,
          sourceType: 'quote',
        },
      })
      handled = handleResult(result, () => undefined)
    }
    return handled
  }

  const handleDeleteStandaloneItem = async (itemId: number) => {
    const deletedItem = standaloneItems.find((item) => item.id === itemId)
    if (!deletedItem) return

    const updatedItems = standaloneItems.filter((item) => item.id !== itemId)
    updateStateOptimistically(updatedItems, true)

    const result = await deleteLineItem({
      variables: {
        id: itemId,
      },
    })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      // TODO: if success, we dont use the response to update state cause we refetch the page query
      // Review if we can avoid that refetching and updateState with the mutation response
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.deleted_successfully'),
        time: 2000,
      })
    } else {
      revertOptimisticState(deletedItem, 'delete')
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
        time: 2000,
      })
    }
  }

  return (
    <Table autoWidth>
      <Header isDraftStatus={isDraftStatus} isProductQuote={isProductQuote} />
      <Body
        standaloneItems={standaloneItems}
        currency={currency}
        isDraftStatus={isDraftStatus}
        isProductQuote={isProductQuote}
        isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
        onUpdateStandaloneItem={handleUpdateStandaloneItem}
        onAddStandaloneItem={handleAddStandaloneItem}
        onDeleteStandaloneItem={handleDeleteStandaloneItem}
        legalEntityCountry={legalEntityCountry}
      />
    </Table>
  )
}

export default QuotePhasesTable
